import {
  ComponentRef,
  EmbeddedViewRef,
  inject,
  Injectable,
  Injector,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ComponentType, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { NavigationStart, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class OverlayService {
  readonly overlay = inject(Overlay);
  private router = inject(Router);
  private overlayRef: OverlayRef | null = null;
  private overlayConfig: OverlayConfig | undefined;
  private scrollPosition: { top: number; left: number } | null = null;

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.overlayRef?.hasAttached()) {
        this.close();
      }
    });
  }

  open<C>(
    content: TemplateRef<C>,
    ref: ViewContainerRef,
    config?: OverlayConfig,
    customInjector?: Injector,
  ): EmbeddedViewRef<C> | null;
  open<C>(
    content: ComponentType<C>,
    ref?: ViewContainerRef,
    config?: OverlayConfig,
    customInjector?: Injector,
  ): ComponentRef<C> | null;
  open<C>(
    content: ComponentType<C> | TemplateRef<C>,
    ref?: ViewContainerRef,
    config?: OverlayConfig,
    customInjector?: Injector,
  ): EmbeddedViewRef<C> | ComponentRef<C> | null {
    if (this.overlayRef?.hasAttached()) {
      this.close();

      return null;
    }

    this.saveCurrentScrollPosition();

    const defaultPositionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();

    this.overlayRef = this.overlay.create({
      positionStrategy: defaultPositionStrategy,
      ...config,
    });
    this.overlayConfig = config;

    const el = this.overlayRef.overlayElement;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('tabindex', '-1');

    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.toggleBodyScroll(true);

    if (content instanceof TemplateRef) {
      if (!ref) return null;
      return this.overlayRef.attach(new TemplatePortal(content, ref));
    }
    return this.overlayRef.attach(new ComponentPortal(content, null, customInjector));
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.toggleBodyScroll(false);
      this.restoreScrollPosition();
    }
  }

  private saveCurrentScrollPosition(): void {
    this.scrollPosition = {
      top: window.scrollY,
      left: window.scrollX,
    };
  }

  private restoreScrollPosition(): void {
    if (this.scrollPosition) {
      window.scrollTo(this.scrollPosition.left, this.scrollPosition.top);
      this.scrollPosition = null;
    }
  }

  private toggleBodyScroll(disable: boolean): void {
    if (this.overlayConfig?.positionStrategy) {
      return;
    }
    document.body.classList.toggle('no-scroll', disable);
  }
}
